<template>
  <!-- Hidden when there is nothing to decide. A card that permanently says
       "0 to review" is furniture; this one only appears when it has a job. -->
  <div v-if="pending > 0" class="card animate-in review-entry">
    <div class="review-entry-head">
      <div>
        <p class="label-caps" style="margin-bottom: 2px;">{{ $t('review.title') }}</p>
        <h3 style="color: var(--text-primary); font-weight: 600; font-size: var(--text-base); margin: 0;">
          {{ $t('review.pending_headline', { count: pending }) }}
        </h3>
      </div>
      <AppBadge v-if="staleness" :variant="staleness.variant">{{ staleness.label }}</AppBadge>
    </div>

    <p class="review-entry-body">{{ $t('review.pending_body') }}</p>

    <AppButton @click="$emit('open')">{{ $t('review.start') }}</AppButton>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { firestoreState } from "../../stores/firestoreStore"
import { countPendingReview, daysSince } from "../../utils/reviewQueue"
import { AppBadge, AppButton } from "../ui"

/**
 * Dashboard entry point for the weekly review. Counts with exactly the same
 * predicate the deck selects with — a card that promises 12 and then opens a
 * deck of 4 destroys the one thing this feature sells, which is that it ends.
 */
defineEmits<{ (e: "open"): void }>()

const { t } = useI18n()

const pending = computed(() => countPendingReview(firestoreState.expenses.value, firestoreState.categories.value))

const staleness = computed(() => {
  const days = daysSince(firestoreState.preferences.value?.lastReviewedAt)
  if (days === null) return { label: t("review.never_reviewed"), variant: "neutral" as const }
  if (days === 0) return null
  if (days >= 14) return { label: t("review.last_reviewed", { days }), variant: "warning" as const }
  return { label: t("review.last_reviewed", { days }), variant: "neutral" as const }
})
</script>

<style scoped>
.review-entry {
  border-left: 3px solid var(--accent);
}

.review-entry-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.review-entry-body {
  font-size: var(--text-sm);
  color: var(--text-secondary);
  line-height: 1.45;
  margin: 0 0 14px;
}
</style>
